import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Drawer, Form, Select, Input, Row, Col, Spin, Popconfirm, message, InputNumber, Tag, AutoComplete, DatePicker, Checkbox } from 'antd';
import moment from 'moment';
import reqwest from 'reqwest';
import { getToken, formatDoseByUnit } from '../../Utils/common';
import config from '../../config';

const { Option } = Select;

function MedicalSchemaMedications({ medicalSchemaId, schemaType = null, onChanged, readOnly = false, autoOpenToken = null }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const keepOpenAfterSaveRef = React.useRef(false);
  const [medForm] = Form.useForm();
  const [medOptions, setMedOptions] = useState([]);
  const [searchingMeds, setSearchingMeds] = useState(false);
  const [selectedMoments, setSelectedMoments] = useState([]);
  const [showExpired, setShowExpired] = useState(false);

  // Drawer to view medications grouped by moment
  const [groupVisible, setGroupVisible] = useState(false);

  // Nested drawer for creating a new medication
  const [createMedVisible, setCreateMedVisible] = useState(false);
  const [createMedSaving, setCreateMedSaving] = useState(false);
  const [createMedForm] = Form.useForm();

  // Edit quantities drawer
  const [editVisible, setEditVisible] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm] = Form.useForm();

  // Dosis predefinidas como fracciones (etiqueta) con su valor equivalente
  const DOSE_PRESETS = [
    { label: '1/4', value: '1/4' },
    { label: '1/2', value: '1/2' },
    { label: '3/4', value: '3/4' },
    { label: '1', value: '1' },
    { label: '1 1/2', value: '1 1/2' },
    { label: '2', value: '2' },
    { label: '2 1/2', value: '2 1/2' },
    { label: '3', value: '3' },
    { label: '3 1/2', value: '3 1/2' },
    { label: '4', value: '4' },
  ];

  function parseDoseValue(val) {
    if (val == null || val === '') return null;
    if (typeof val === 'number') return val;
    const s = String(val).trim();
    // Mixed number e.g., "2 1/2"
    const mixedMatch = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
      const whole = parseFloat(mixedMatch[1]);
      const num = parseFloat(mixedMatch[2]);
      const den = parseFloat(mixedMatch[3]);
      if (den !== 0) return whole + (num / den);
    }
    // Pure fraction e.g., "1/2"
    const fracMatch = s.match(/^(\d+)\/(\d+)$/);
    if (fracMatch) {
      const num = parseFloat(fracMatch[1]);
      const den = parseFloat(fracMatch[2]);
      if (den !== 0) return num / den;
    }
    // Decimal number
    const n = parseFloat(s.replace(',', '.'));
    if (!isNaN(n)) return n;
    return NaN;
  }

  const MOMENT_OPTIONS = [
    { value: 'DES', label: 'Desayuno' },
    { value: 'ALM', label: 'Almuerzo' },
    { value: 'MER', label: 'Merienda' },
    { value: 'CEN', label: 'Cena' },
    { value: 'PER', label: 'Personalizado' },
  ];

  const canOperate = !!medicalSchemaId && !readOnly;
  // Auto-open handler when parent requests it (after schema creation)
  const lastAutoOpenRef = React.useRef(null);
  useEffect(() => {
    if (autoOpenToken && autoOpenToken !== lastAutoOpenRef.current) {
      lastAutoOpenRef.current = autoOpenToken;
      if (canOperate) {
        onOpenDrawer();
      }
    }
  }, [autoOpenToken, canOperate, medicalSchemaId]);

  const fetchList = (pageArg, pageSizeArg, includeExpiredArg) => {
    if (!medicalSchemaId) return;
    setLoading(true);
    const page = pageArg || pagination.current || 1;
    const pageSize = pageSizeArg || pagination.pageSize || 10;
    const offset = (page - 1) * pageSize;
    const expired = includeExpiredArg !== undefined ? includeExpiredArg : showExpired;
    const baseUrl = `${config.api_url}/medicalschemamedicaton/?medical_schema=${medicalSchemaId}${expired ? '&include_expired=true' : ''}`;
    const url = `${baseUrl}&limit=${pageSize}&offset=${offset}`;
    reqwest({
      url,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(response => {
      // The default DRF pagination may or may not be enabled; support both formats
      if (Array.isArray(response)) {
        // No server pagination: paginate on client side
        const total = response.length;
        const start = offset;
        const end = start + pageSize;
        setData(response.slice(start, end));
        setPagination(prev => ({ ...prev, current: page, pageSize, total }));
      } else {
        const results = response.results || [];
        const count = typeof response.count === 'number' ? response.count : results.length;
        setData(results);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: count }));
      }
      setLoading(false);
    }).catch(error => {
      setLoading(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        message.error(`Error al cargar medicamentos del esquema: ${error.status}`);
      }
    });
  };

  useEffect(() => {
    // reset to first page when schema or expired filter changes
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList(1, pagination.pageSize, showExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalSchemaId, showExpired]);

  const handleTableChange = (pag) => {
    const nextCurrent = pag.current || 1;
    const nextPageSize = pag.pageSize || pagination.pageSize || 10;
    fetchList(nextCurrent, nextPageSize);
  };

  const onOpenDrawer = () => {
    medForm.resetFields();
    setDrawerVisible(true);
  };

  const onCloseDrawer = () => {
    setDrawerVisible(false);
  };

  const searchMedications = (searchText) => {
    setSearchingMeds(true);
    reqwest({
      url: `${config.api_url}/medications/?search=${encodeURIComponent(searchText)}&limit=1000`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(response => {
      setSearchingMeds(false);
      const results = response.results || [];
      const options = results.map(m => {
        const pres = (m.presentation || '').trim();
        const label = pres ? `${m.name} — ${pres}` : `${m.name}`;
        return { value: m.id, label };
      });
      setMedOptions(options);
    }).catch(error => {
      setSearchingMeds(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        message.error(`Error al buscar medicamentos: ${error.status}`);
      }
    });
  };

  // Ensure a medication id is selected
  const ensureMedicationId = async (values) => {
    return values.medication;
  };

  const onSubmit = async (values) => {
    if (!medicalSchemaId || readOnly) return;
    setSaving(true);
    try {
      const medicationId = await ensureMedicationId(values);
      if (!medicationId) {
        setSaving(false);
        message.error('Debe seleccionar o crear un medicamento');
        return;
      }

      const selectedMomentsLocal = Array.isArray(values.moments) ? values.moments : [];

      // Must select at least one moment
      if (selectedMomentsLocal.length === 0) {
        setSaving(false);
        message.error('Debe seleccionar al menos un horario');
        return;
      }

      // If PER (personalizado) is selected, require custom_moment
      if (selectedMomentsLocal.includes('PER') && !values.custom_moment) {
        setSaving(false);
        message.error('Debe ingresar el detalle personalizado para el horario "Personalizado"');
        return;
      }

      // Validate a dose per selected moment
      const doses = values.doses || {};
      const parsedDoses = {};
      for (const m of selectedMomentsLocal) {
        const d = doses[m];
        if (d == null || d === '') {
          setSaving(false);
          message.error('Debe ingresar una dosis para cada horario seleccionado');
          return;
        }
        const parsed = parseDoseValue(d);
        if (parsed === null || isNaN(parsed)) {
          setSaving(false);
          message.error(`La dosis ingresada para ${m} no es válida`);
          return;
        }
        parsedDoses[m] = parsed;
      }

      const basePayload = {
        medical_schema: medicalSchemaId,
        medication: medicationId,
        until_date: values.until_date ? values.until_date.format('YYYY-MM-DD') : null,
      };

      // Create one request per selected moment
      await Promise.all(selectedMomentsLocal.map((m) => {
        const payload = {
          ...basePayload,
          dose: parsedDoses[m],
          moment: m,
          custom_moment: m === 'PER' ? (values.custom_moment || null) : null,
        };
        return reqwest({
          url: `${config.api_url}/medicalschemamedicaton/`,
          method: 'post',
          type: 'json',
          data: payload,
          headers: { 'Authorization': `Token ${getToken()}` },
        });
      }));

      setSaving(false);
      const keepOpen = !!keepOpenAfterSaveRef.current;
      if (keepOpen) {
        medForm.resetFields();
        message.success('Medicamento(s) agregado(s). Puede seguir cargando.');
        // Reset the flag after we've captured it
        keepOpenAfterSaveRef.current = false;
      } else {
        setDrawerVisible(false);
        medForm.resetFields();
        message.success('Medicamento(s) agregado(s) al esquema');
      }
      fetchList();
      // Notificar al padre solo cuando se cierra (para evitar que el padre re-renderice y cierre el drawer)
      if (!keepOpen && onChanged) onChanged('ADD');
    } catch (error) {
      setSaving(false);
      if (error && error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        const status = (error && error.status) || '';
        message.error(`Error al agregar medicamento: ${status}`);
      }
    }
  };

  const onDelete = (record) => {
    if (readOnly) return;
    setLoading(true);
    reqwest({
      url: `${config.api_url}/medicalschemamedicaton/${record.id}/`,
      method: 'delete',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(() => {
      message.success('Medicamento eliminado del esquema');
      fetchList();
      if (onChanged) onChanged('DEL');
    }).catch(error => {
      setLoading(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        message.error(`Error al eliminar: ${error.status}`);
      }
    });
  };

  const openEdit = (record) => {
    setEditRecord(record);
    editForm.setFieldsValue({
      dose: record.dose != null && record.dose !== '' ? String(record.dose) : undefined,
      moment: record.moment || null,
      custom_moment: record.custom_moment || null,
      until_date: record.until_date ? moment(record.until_date) : null,
    });
    setEditVisible(true);
  };

  const closeEdit = () => {
    setEditVisible(false);
    setEditRecord(null);
    editForm.resetFields();
  };

  const onUpdate = (values) => {
    if (readOnly) return;
    if (!editRecord) return;
    setEditSaving(true);
    let doseVal = values.dose;
    if (doseVal != null && doseVal !== '') {
      doseVal = parseDoseValue(doseVal);
      if (doseVal === null || isNaN(doseVal)) {
        setEditSaving(false);
        message.error('La dosis ingresada no es válida');
        return;
      }
    } else {
      doseVal = null;
    }
    const payload = {
      dose: doseVal,
      moment: values.moment || null,
      custom_moment: values.moment === 'PER' ? (values.custom_moment || null) : null,
      until_date: values.until_date ? values.until_date.format('YYYY-MM-DD') : null,
    };
    reqwest({
      url: `${config.api_url}/medicalschemamedicaton/${editRecord.id}/`,
      method: 'patch',
      type: 'json',
      data: payload,
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then((resp) => {
      setEditSaving(false);
      setEditVisible(false);
      setEditRecord(null);
      editForm.resetFields();
      message.success('Medicamento actualizado');
      fetchList();
      if (onChanged) onChanged('UPD');
    }).catch(error => {
      setEditSaving(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        const status = (error && error.status) || '';
        message.error(`Error al actualizar: ${status}`);
      }
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '8%' },
    { title: 'Medicamento', dataIndex: 'medication_name', key: 'medication_name', render: (val, record) => {
      const isExpired = record.until_date && moment(record.until_date).isBefore(moment(), 'day');
      return (
        <span>
          {val || `#${record.medication}`}
          {isExpired ? <Tag color="red" style={{ marginLeft: 8 }}>Vencido</Tag> : null}
        </span>
      );
    } },
    { title: 'Presentación', dataIndex: 'medication_presentation', key: 'medication_presentation', width: '16%' },
    { title: 'Dosis', dataIndex: 'dose', key: 'dose', width: '10%', render: (v) => {
          if (v == null || v === '') return '-';
          const disp = formatDoseByUnit(v, null);
          return `${disp}`;
        } },
    { title: 'Horario', dataIndex: 'moment', key: 'moment', width: '14%',
      render: (val, record) => {
        const map = { DES: 'Desayuno', ALM: 'Almuerzo', MER: 'Merienda', CEN: 'Cena', PER: 'Personalizado' };
        if (!val) return '-';
        if (val === 'PER') {
          return record.custom_moment ? `Personalizado: ${record.custom_moment}` : 'Personalizado';
        }
        return map[val] || val;
      }
    },
    { title: 'Hasta', dataIndex: 'until_date', key: 'until_date', width: '12%', 
      hidden: schemaType === 'REF',
      render: (v) => v ? moment(v).format('DD/MM/YYYY') : '-' 
    },
    { title: 'Días', key: 'days', width: '8%', 
      hidden: schemaType === 'REF',
      render: (_, record) => {
        if (!record.until_date || !record.created_at) return '-';
        const start = moment(record.created_at).startOf('day');
        const end = moment(record.until_date).startOf('day');
        const diff = end.diff(start, 'days');
        return diff >= 0 ? diff + 1 : '-';
      }
    },
  ];

  return (
    <Card title="Medicamentos del Esquema" extra={
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Checkbox checked={showExpired} onChange={e => setShowExpired(e.target.checked)}>Mostrar vencidos</Checkbox>
        <Button onClick={() => setGroupVisible(true)} disabled={data.length === 0}>Ver por horarios</Button>
        <Button type="primary" onClick={onOpenDrawer} disabled={!canOperate}>Agregar</Button>
      </div>
    }>
      {!canOperate && (
        <div style={{ marginBottom: 12, color: '#999' }}>
          { !medicalSchemaId ? 'Seleccione una internación para gestionar su esquema.' : 'Este esquema no está activo. No es posible agregar o modificar medicamentos.' }
        </div>
      )}
      <Spin spinning={loading}>
        <Table
          rowKey={r => r.id}
          columns={columns.filter(c => !c.hidden)}
          dataSource={data}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => { if (!readOnly) openEdit(record); },
            style: { cursor: readOnly ? 'default' : 'pointer' }
          })}
        />
      </Spin>

      <Drawer
        title="Agregar medicamento"
        width={520}
        visible={drawerVisible}
        open={drawerVisible}
        onClose={onCloseDrawer}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={onCloseDrawer} style={{ marginRight: 8 }}>Cancelar</Button>
            <Button loading={saving} onClick={() => { keepOpenAfterSaveRef.current = true; medForm.submit(); }} style={{ marginRight: 8 }}>
              Guardar y Seguir Cargando
            </Button>
            <Button type="primary" loading={saving} onClick={() => { keepOpenAfterSaveRef.current = false; medForm.submit(); }}>Agregar</Button>
          </div>
        }
      >
        <Form form={medForm} layout="vertical" onFinish={onSubmit}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="medication" label="Seleccionar medicamento existente">
                <Select
                  showSearch
                  placeholder="Buscar por nombre o presentación"
                  filterOption={false}
                  onSearch={searchMedications}
                  notFoundContent={searchingMeds ? <Spin size="small" /> : 'Sin resultados'}
                  allowClear
                >
                  {medOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {schemaType !== 'REF' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.until_date !== curr.until_date}>
                  {({ getFieldValue }) => {
                    const untilDate = getFieldValue('until_date');
                    let daysLabel = '';
                    if (untilDate) {
                      const start = moment().startOf('day');
                      const end = moment(untilDate).startOf('day');
                      const diff = end.diff(start, 'days');
                      if (diff >= 0) {
                        daysLabel = `(${diff + 1} día${diff + 1 !== 1 ? 's' : ''})`;
                      }
                    }
                    return (
                      <Form.Item name="until_date" label={<span>Hasta (opcional) <span style={{ color: '#999', fontWeight: 'normal', marginLeft: 8 }}>{daysLabel}</span></span>}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
          )}
          {/*<Row gutter={16}>*/}
          {/*  <Col span={24}>*/}
          {/*    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>*/}
          {/*      <span>¿No encuentra el medicamento?</span>*/}
          {/*      <Button onClick={() => { createMedForm.resetFields(); setCreateMedVisible(true); }}>*/}
          {/*        Crear nuevo medicamento*/}
          {/*      </Button>*/}
          {/*    </div>*/}
          {/*  </Col>*/}
          {/*</Row>*/}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="moments" label="Horarios" rules={[{ required: true, message: 'Seleccione al menos un horario' }]}>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Seleccione horarios"
                  onChange={(vals) => setSelectedMoments(vals || [])}
                >
                  {MOMENT_OPTIONS.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const selected = getFieldValue('moments') || [];
                  return selected.includes('PER') ? (
                    <Form.Item name="custom_moment" label="Detalle personalizado" rules={[{ required: true, message: 'Ingrese el detalle' }]}>
                      <Input placeholder="Ej: Cada 8 horas" />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const selected = getFieldValue('moments') || [];
                  const map = { DES: 'Desayuno', ALM: 'Almuerzo', MER: 'Merienda', CEN: 'Cena', PER: 'Personalizado' };
                  if (!selected.length) {
                    return (
                      <div style={{ marginTop: 8, color: '#999' }}>
                        Seleccione al menos un horario para ingresar las dosis.
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ marginBottom: 8, color: '#666' }}>Dosis por horario seleccionado</div>
                      {selected.map((m) => (
                        <Row key={m} gutter={16}>
                          <Col span={12}>
                            <div style={{ paddingTop: 30 }}>
                              <Tag color="blue">{map[m] || m}</Tag>
                            </div>
                          </Col>
                          <Col span={12}>
                            <Form.Item name={["doses", m]} label={`Dosis para ${map[m] || m}`} rules={[{ required: true, message: 'Ingrese la dosis' }]}>
                              <AutoComplete
                                options={DOSE_PRESETS}
                                placeholder="Seleccione o escriba la dosis (ej: 1/2, 2.5, 3 1/2)"
                                filterOption={(inputValue, option) => (option?.value || '').toLowerCase().includes(String(inputValue).toLowerCase())}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      {/* Grouped by moment drawer */}
      <Drawer
        title="Medicamentos por horario"
        width={680}
        visible={groupVisible}
        onClose={() => setGroupVisible(false)}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          Se muestran los medicamentos agrupados por <b>Horario</b>. Dentro de cada sección, cada fila indica la <b>Medicación</b>, su <b>Dosis</b> y, si corresponde, el <b>Detalle</b>.
        </div>
        {(() => {
          const order = [
            { code: 'DES', title: 'Desayuno', color: 'gold' },
            { code: 'ALM', title: 'Almuerzo', color: 'green' },
            { code: 'MER', title: 'Merienda', color: 'cyan' },
            { code: 'CEN', title: 'Cena', color: 'purple' },
            { code: 'PER', title: 'Personalizado', color: 'volcano' },
          ];
          const sectionWrap = {
            marginBottom: 16,
            padding: 12,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            background: '#fff'
          };
          const headerRow = {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8
          };
          const itemRow = {
            padding: '8px 0',
            borderTop: '1px dashed #f0f0f0'
          };
          const label = { color: '#888' };
          const valueBold = { fontWeight: 500 };
          const momentMap = {
            DES: 'Desayuno',
            ALM: 'Almuerzo',
            MER: 'Merienda',
            CEN: 'Cena',
            PER: 'Personalizado',
          };
          return (
            <div>
              {order.map(({ code, title, color }) => {
                const items = data.filter(r => r.moment === code);
                if (items.length === 0) return null;
                return (
                  <div key={code} style={sectionWrap}>
                    <div style={headerRow}>
                      <div>
                        <Tag color={color} style={{ marginRight: 8 }}>{momentMap[code] || title}</Tag>
                        <span style={{ color: '#999' }}>Horario</span>
                      </div>
                      <Tag>{items.length} medicación(es)</Tag>
                    </div>
                    <div>
                      {items.map((it, idx) => {
                        const dose = it.dose != null && it.dose !== '' ? it.dose : null;
                        const doseTxt = dose ? `${formatDoseByUnit(dose, null)}` : '-';
                        const medName = it.medication_name || `#${it.medication}`;
                        const pres = (it.medication_presentation || '').trim();
                        return (
                          <div key={it.id} style={{ ...itemRow, borderTop: idx === 0 ? 'none' : itemRow.borderTop }}>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 260 }}>
                                <div style={label}>Medicación</div>
                                <div style={valueBold}>{medName}{pres ? ` — ${pres}` : ''}</div>
                              </div>
                              <div style={{ minWidth: 140 }}>
                                <div style={label}>Dosis</div>
                                <div>{doseTxt}</div>
                              </div>
                              {it.until_date && schemaType !== 'REF' && (
                                <div style={{ minWidth: 120 }}>
                                  <div style={label}>Hasta</div>
                                  <div>{moment(it.until_date).format('DD/MM/YYYY')}</div>
                                </div>
                              )}
                              {code === 'PER' && (
                                <div style={{ minWidth: 200 }}>
                                  <div style={label}>Detalle</div>
                                  <div>{it.custom_moment || '-'}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Drawer>

      {/* Edit drawer: update dose/unit of an existing schema medication */}
      <Drawer
        title={editRecord ? `Editar medicamento del esquema #${editRecord.id}` : 'Editar medicamento del esquema'}
        width={520}
        visible={editVisible}
        onClose={closeEdit}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {!readOnly && editRecord ? (
                <Popconfirm
                  title="¿Eliminar medicamento del esquema?"
                  onConfirm={() => { onDelete(editRecord); closeEdit(); }}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button danger>Eliminar</Button>
                </Popconfirm>
              ) : null}
            </div>
            <div>
              <Button onClick={closeEdit} style={{ marginRight: 8 }}>Cancelar</Button>
              <Button type="primary" loading={editSaving} onClick={() => editForm.submit()}>Guardar</Button>
            </div>
          </div>
        }
      >
        <Form form={editForm} layout="vertical" onFinish={onUpdate}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="dose" label="Dosis">
                <AutoComplete
                  options={DOSE_PRESETS}
                  placeholder="Seleccione o escriba la dosis (ej: 1/2, 2.5, 3 1/2)"
                  filterOption={(inputValue, option) => (option?.value || '').toLowerCase().includes(String(inputValue).toLowerCase())}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          {schemaType !== 'REF' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.until_date !== curr.until_date}>
                  {({ getFieldValue }) => {
                    const untilDate = getFieldValue('until_date');
                    let daysLabel = '';
                    if (untilDate && editRecord?.created_at) {
                      const start = moment(editRecord.created_at).startOf('day');
                      const end = moment(untilDate).startOf('day');
                      const diff = end.diff(start, 'days');
                      if (diff >= 0) {
                        daysLabel = `(${diff + 1} día${diff + 1 !== 1 ? 's' : ''})`;
                      }
                    }
                    return (
                      <Form.Item name="until_date" label={<span>Hasta (opcional) <span style={{ color: '#999', fontWeight: 'normal', marginLeft: 8 }}>{daysLabel}</span></span>}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="moment" label="Horario">
                <Select allowClear placeholder="Seleccione horario">
                  {MOMENT_OPTIONS.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  getFieldValue('moment') === 'PER' ? (
                    <Form.Item name="custom_moment" label="Detalle personalizado" rules={[{ required: true, message: 'Ingrese el detalle' }]}>
                      <Input placeholder="Ej: Cada 8 horas" />
                    </Form.Item>
                  ) : null
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      {/* Nested drawer: Create new medication */}
      <Drawer
        title="Nuevo Medicamento"
        width={520}
        visible={createMedVisible}
        onClose={() => { setCreateMedVisible(false); createMedForm.resetFields(); }}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => { setCreateMedVisible(false); createMedForm.resetFields(); }} style={{ marginRight: 8 }}>Cancelar</Button>
            <Button type="primary" loading={createMedSaving} onClick={() => createMedForm.submit()}>Crear</Button>
          </div>
        }
      >
        <Form form={createMedForm} layout="vertical" onFinish={async (vals) => {
          setCreateMedSaving(true);
          try {
            const payload = {
              name: vals.name,
              presentation: vals.presentation || null,
              description: vals.description || null,
              current_stock: vals.current_stock != null ? vals.current_stock : 0,
            };
            const created = await reqwest({
              url: `${config.api_url}/medications/`,
              method: 'post',
              type: 'json',
              data: payload,
              headers: { 'Authorization': `Token ${getToken()}` },
            });
            setCreateMedSaving(false);
            setCreateMedVisible(false);
            createMedForm.resetFields();
            message.success('Medicamento creado');
            // Add to options and auto-select (include presentación if provided)
            const pres = (created.presentation || '').trim();
            const newOption = { value: created.id, label: pres ? `${created.name} — ${pres}` : `${created.name}` };
            setMedOptions(prev => [newOption, ...prev]);
            medForm.setFieldsValue({ medication: created.id });
          } catch (error) {
            setCreateMedSaving(false);
            if (error && error.status === 401) {
              sessionStorage.removeItem('token');
              sessionStorage.removeItem('user');
              window.location.href = '/login';
            } else {
              const status = (error && error.status) || '';
              message.error(`Error al crear medicamento: ${status}`);
            }
          }
        }}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="presentation" label="Presentación">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="current_stock" label="Stock Actual">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
}

export default MedicalSchemaMedications;
